/**
 * API errors, in the reader's language.
 *
 * Only codes whose server message is not good enough on its own are listed.
 * Everything else keeps the server's wording: it is more specific than a
 * generic translation would be, and a second source of truth for every message
 * would guarantee the two drift apart.
 *
 * The server stays English on purpose — an error also has to be readable in a
 * log, and a log written in whatever language the last request happened to
 * carry is not.
 */
import { ApiError } from '@/lib/api/client';

import { translate, type MessageKey } from './translate';

const BY_CODE: Record<string, MessageKey> = {
  SUBTASKS_INCOMPLETE: 'error.blockedBySubtasks',
  USERNAME_TAKEN: 'error.usernameTaken',
  NOT_FOUND: 'error.notFound',
  FORBIDDEN: 'error.forbidden',
  NETWORK_ERROR: 'error.offline',
};

export function describeError(error: ApiError): string {
  // Telegram's rate limit carries a number worth showing, so it is translated
  // with the wait rather than mapped to a fixed sentence.
  if (error.code === 'TG_FLOOD_WAIT') {
    const seconds = Number(error.details?.seconds);
    if (Number.isFinite(seconds)) {
      return translate('error.tgFloodWait', { seconds: String(seconds) });
    }
  }
  const key = error.code ? BY_CODE[error.code] : undefined;
  return key ? translate(key) : error.message;
}

/** `describeError` for anything, with a fallback for non-API failures. */
export function describeFailure(error: unknown, fallback: MessageKey): string {
  return error instanceof ApiError ? describeError(error) : translate(fallback);
}
